import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { X, Server, Terminal as TerminalIcon, Monitor } from 'lucide-react';
import { reqPost } from '../../Service';

/**
 * 호스트 추가/수정 모달.
 *
 * Props:
 *   open           : boolean
 *   onClose()      : 모달 닫기
 *   onSaved(host)  : 저장 후 콜백 (목록 새로고침)
 *   type           : 'Skynet' | 'AWS' | 'AntsNest' — Compute 탭에서 전달
 *   initial        : 수정 시 기존 호스트 객체 (없으면 신규)
 */
export default function HostAddEditModal({ open, onClose, onSaved, type, initial }) {
  const isEdit = !!initial?.infra_id;
  const externalTypes = ['IWINV', 'Hetzner', 'AWS', 'External'];
  const isExternal = externalTypes.includes(type);

  const [form, setForm] = useState({
    title: '',
    domain: '',
    port: '',
    info: '',
    defaultAccount: '',
    os_type: 'ubuntu',
    console_proto: 'ssh'
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    if (initial) {
      setForm({
        provider: initial.infra_type || type,
        title: initial.infra_title || '',
        domain: initial.infra_domain || '',
        port: String(initial.infra_port || ''),
        info: initial.infra_info || '',
        defaultAccount: initial.default_account || '',
        os_type: initial.os_type || 'ubuntu',
        console_proto: initial.console_proto || 'ssh'
      });
    } else {
      setForm({
        provider: type,
        title: '',
        domain: '',
        port: '22',
        info: '',
        defaultAccount: '',
        os_type: 'ubuntu',
        console_proto: 'ssh'
      });
    }
    setError('');
  }, [open, initial, type]);

  // OS 타입 선택 시 콘솔 프로토콜 자동 추천
  const handleOsChange = (os_type) => {
    const proto = os_type === 'windows' ? 'rdp' : 'ssh';
    const port = os_type === 'windows'
      ? (form.port === '22' || !form.port ? '3389' : form.port)
      : (form.port === '3389' || !form.port ? '22' : form.port);
    setForm(f => ({ ...f, os_type, console_proto: proto, port }));
  };

  if (!open) return null;

  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (saving) return;
    setError('');
    if (!form.title.trim()) return setError('호스트 이름을 입력하세요.');
    if (!form.domain.trim()) return setError('도메인 또는 IP를 입력하세요.');
    if (!form.port.trim()) return setError('포트를 입력하세요.');

    setSaving(true);
    const url = isEdit ? '/api/hostUpdate' : '/api/hostAdd';
    const body = {
      type: isExternal ? form.provider : type,
      title: form.title.trim(),
      domain: form.domain.trim(),
      port: form.port.trim(),
      info: form.info.trim(),
      defaultAccount: form.defaultAccount.trim(),
      os_type: form.os_type,
      console_proto: form.console_proto,
      id: initial?.infra_id
    };
    try {
      reqPost(url, body, () => {
        setSaving(false);
        onSaved?.();
        onClose?.();
      }, err => {
        setSaving(false);
        setError(err.message || '저장 실패');
      });
    } catch (err) {
      setSaving(false);
      setError(err.message || '저장 실패');
    }
  };

  return (
    <Overlay>
      <Modal>
        <Header>
          <h3>{isEdit ? '호스트 수정' : '호스트 추가'}</h3>
          <CloseBtn onClick={onClose}><X size={18} /></CloseBtn>
        </Header>

        <Body>
          {isExternal && <Row>
            <Label htmlFor="host-provider">공급사</Label>
            <Select id="host-provider" value={form.provider || type} onChange={e => update('provider', e.target.value)}>
              {externalTypes.map(value => <option key={value} value={value}>{value === 'External' ? '기타 외부 클라우드' : value}</option>)}
            </Select>
          </Row>}
          <Row>
            <Label>이름 *</Label>
            <Input
              autoFocus
              placeholder="예: web-prd-01"
              value={form.title}
              onChange={e => update('title', e.target.value)}
            />
          </Row>

          <Row>
            <Label>도메인 / IP *</Label>
            <Input
              placeholder="예: 10.0.1.23 또는 host.example.com"
              value={form.domain}
              onChange={e => update('domain', e.target.value)}
            />
          </Row>

          <RowFlex>
            <Col>
              <Label>OS *</Label>
              <Select value={form.os_type} onChange={e => handleOsChange(e.target.value)}>
                <option value="ubuntu">Ubuntu</option>
                <option value="centos">CentOS / RHEL</option>
                <option value="debian">Debian</option>
                <option value="windows">Windows</option>
                <option value="other">기타 Linux/Unix</option>
              </Select>
            </Col>
            <Col>
              <Label>콘솔 프로토콜 *</Label>
              <Select value={form.console_proto} onChange={e => update('console_proto', e.target.value)}>
                <option value="ssh">SSH (브라우저 콘솔)</option>
                <option value="rdp">RDP (mstsc)</option>
              </Select>
            </Col>
            <Col style={{ maxWidth: 110 }}>
              <Label>포트 *</Label>
              <Input
                type="number"
                value={form.port}
                onChange={e => update('port', e.target.value)}
              />
            </Col>
          </RowFlex>

          <Row>
            <Label>설명</Label>
            <Textarea
              rows={2}
              placeholder="이 호스트의 용도/특이사항 (선택)"
              value={form.info}
              onChange={e => update('info', e.target.value)}
            />
          </Row>

          <Row>
            <Label>기본 계정 (선택)</Label>
            <Input
              placeholder="기본으로 표시할 계정 라벨 (예: ubuntu)"
              value={form.defaultAccount}
              onChange={e => update('defaultAccount', e.target.value)}
            />
            <Hint>
              실제 SSH 계정/비밀번호는 카드의 ⚙️(설정) 에서 추가합니다. Phase 1 은 비밀번호 인증만 지원.
            </Hint>
          </Row>

          {error && <ErrorMsg>{error}</ErrorMsg>}

          <ProtoBadge proto={form.console_proto}>
            {form.console_proto === 'ssh' ? (
              <><TerminalIcon size={14} /> 카드의 [연결하기] 가 브라우저 SSH 콘솔로 동작합니다</>
            ) : (
              <><Monitor size={14} /> 카드의 [연결하기] 가 mstsc 로 RDP 연결합니다 (앤츠 클라우드 클라이언트 필요)</>
            )}
          </ProtoBadge>
        </Body>

        <Footer>
          <CancelBtn onClick={onClose} disabled={saving}>취소</CancelBtn>
          <SaveBtn onClick={handleSave} disabled={saving}>
            {saving ? '저장 중...' : (isEdit ? '수정' : '추가')}
          </SaveBtn>
        </Footer>
      </Modal>
    </Overlay>
  );
}

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const Modal = styled.div`
  background: white;
  border-radius: 10px;
  width: 560px;
  max-width: calc(100% - 32px);
  max-height: calc(100vh - 64px);
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  text-align: left;
  @media(max-width:768px){max-height:calc(100dvh - 24px);}
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 22px;
  border-bottom: 1px solid #eaeaea;
  h3 { margin: 0; font-size: 16px; font-weight: 600; color: #1a1a1a; }
`;

const CloseBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #666;
  display: flex;
  align-items: center;
  padding: 4px;
  border-radius: 4px;
  &:hover { background: #f5f5f5; }
`;

const Body = styled.div`
  padding: 18px 22px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const Row = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const RowFlex = styled.div`
  display: flex;
  gap: 10px;
  & > * { flex: 1; }
  @media(max-width:768px){flex-direction:column;>*{max-width:none !important;min-width:0;}}
`;

const Col = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 12px;
  font-weight: 600;
  color: #444;
`;

const Input = styled.input`
  padding: 8px 10px;
  border: 1px solid #d6dee8;
  border-radius: 5px;
  font-size: 14px;
  outline: none;
  &:focus { border-color: #0066ff; }
`;

const Textarea = styled.textarea`
  padding: 8px 10px;
  border: 1px solid #d6dee8;
  border-radius: 5px;
  font-size: 14px;
  outline: none;
  resize: vertical;
  font-family: inherit;
  &:focus { border-color: #0066ff; }
`;

const Select = styled.select`
  padding: 8px 10px;
  border: 1px solid #d6dee8;
  border-radius: 5px;
  font-size: 14px;
  background: white;
  outline: none;
  &:focus { border-color: #0066ff; }
`;

const Hint = styled.div`
  font-size: 11px;
  color: #888;
`;

const ProtoBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 10px;
  background: ${({ proto }) => proto === 'ssh' ? '#e6f4ea' : '#e8f0fe'};
  color: ${({ proto }) => proto === 'ssh' ? '#1e7e34' : '#1a73e8'};
  border-radius: 5px;
  font-size: 12px;
`;

const ErrorMsg = styled.div`
  color: #d93025;
  font-size: 13px;
  background: #fce8e6;
  padding: 8px 10px;
  border-radius: 5px;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 14px 22px;
  border-top: 1px solid #eaeaea;
`;

const CancelBtn = styled.button`
  padding: 8px 16px;
  background: white;
  color: #444;
  border: 1px solid #d6dee8;
  border-radius: 5px;
  cursor: pointer;
  font-size: 14px;
  &:hover { background: #f5f5f5; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const SaveBtn = styled.button`
  padding: 8px 18px;
  background: #0066ff;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  &:hover { background: #0052cc; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;
